import {
  AiOutlineDollar,
  AiOutlineExperiment,
  AiOutlineIdcard,
  AiOutlinePoundCircle,
  AiOutlineSetting,
} from "react-icons/ai";
import { Link } from "react-router";

import { featureFlags } from "shell/platform/config/featureFlags";
import { pathsSchema } from "shell/platform/config/paths";

import type { MenuDeps, MenuItem } from "../types";

import { buildSmartClientDeprecationChildren } from "./buildSmartClientDeprecationMenu";
import { compactMenuItems } from "./compactMenuItems";

const { isProd, showSmartClientDeprecation } = featureFlags;

const buildProductsChildren = ({ t }: MenuDeps): MenuItem[] =>
  compactMenuItems([
    {
      key: "financial-products",
      label: (
        <Link to={pathsSchema.financialProducts.index}>
          {t("common:components.mainMenu.financialProducts")}
        </Link>
      ),
    },
    {
      key: "instrument-products",
      label: (
        <Link to={pathsSchema.instrumentProducts.index}>
          {t("common:components.mainMenu.instrumentProducts")}
        </Link>
      ),
    },
  ]);

const buildSandboxChildren = ({ t }: MenuDeps): MenuItem[] =>
  compactMenuItems([
    {
      key: "sandbox-audit-log",
      label: (
        <Link to={pathsSchema.sandboxAdmin.audit}>
          {t("common:components.auditLog.title")}
        </Link>
      ),
    },
    {
      key: "sandbox-user-management",
      label: (
        <Link to={pathsSchema.sandboxAdmin.userManagement}>
          {t("common:labels.users")}
        </Link>
      ),
    },
    {
      key: "sandbox-domain-lists",
      label: (
        <Link to={pathsSchema.sandboxAdmin.domainLists}>
          {t("common:components.mainMenu.domainLists")}
        </Link>
      ),
    },
  ]);

const buildSystemAdminSectionChildren = (deps: MenuDeps): MenuItem[] => {
  const { t, hasRole } = deps;

  return compactMenuItems([
    !isProd &&
      hasRole(["Manager", "Internal"]) && {
        key: "all-clients",
        label: (
          <Link to={pathsSchema.clients.index}>
            {t("common:components.mainMenu.allClients")}
          </Link>
        ),
      },
    !isProd &&
      hasRole(["Manager", "Internal"]) && {
        key: "all-sub-clients",
        label: (
          <Link to={pathsSchema.clients.subClients}>
            {t("common:components.mainMenu.allSubClients")}
          </Link>
        ),
      },
    !isProd &&
      hasRole(["Internal"]) && {
        key: "client-spaces",
        label: (
          <Link to={pathsSchema.commonServices.clientSpaces.dashboard}>
            {t("commonServices:breadcrumbs.clientSpaces.index")}
          </Link>
        ),
      },
    !isProd &&
      hasRole(["Internal"]) && {
        key: "feature-flags",
        label: (
          <Link to={pathsSchema.featureFlags.index}>
            {t("common:components.mainMenu.featureFlags")}
          </Link>
        ),
      },
    !isProd &&
      hasRole(["Internal"]) && {
        key: "key-management",
        label: (
          <Link to={pathsSchema.keyManagement.index}>
            {t("common:components.mainMenu.keyManagement")}
          </Link>
        ),
      },
    !isProd &&
      featureFlags.enableAdminAccounts &&
      hasRole(["Internal"]) && {
        key: "accounts",
        label: (
          <Link to={pathsSchema.accounts.index}>
            {t("common:components.mainMenu.accounts")}
          </Link>
        ),
      },
    hasRole(["Internal", "Organisation Manager"]) && {
      key: "organizations",
      label: t("clientAccess:components.mainMenu.organizations"),
      children: [
        {
          key: "organizations-list",
          label: (
            <Link to={pathsSchema.organizations.index}>
              {t("clientAccess:components.mainMenu.dashboard")}
            </Link>
          ),
        },
      ],
    },
    !isProd &&
      hasRole(["Manager", "Internal"]) && {
        key: "global-rules",
        label: t("common:components.mainMenu.products"),
        children: buildProductsChildren(deps),
      },
    featureFlags.enableSandbox &&
      hasRole(["Internal"]) && {
        key: "sandbox",
        label: t("common:components.mainMenu.sandbox"),
        children: buildSandboxChildren(deps),
      },
  ]);
};

export const buildConsoleMenuItems = (deps: MenuDeps): MenuItem[] => {
  const { t, hasRole, isInternal, colorPrimary } = deps;

  return compactMenuItems([
    !isProd &&
      showSmartClientDeprecation &&
      hasRole(["Internal"]) && {
        key: "smart-client-deprecation",
        label: "Smart Client Deprecation",
        icon: <AiOutlineSetting color={colorPrimary} />,
        children: buildSmartClientDeprecationChildren(t),
      },
    !isProd &&
      isInternal && {
        key: "customers",
        label: (
          <Link to={pathsSchema.servicing.customers.index}>
            {t("common:components.mainMenu.customers")}
          </Link>
        ),
        icon: <AiOutlineIdcard color={colorPrimary} />,
      },
    {
      key: "admin",
      label: t("common:components.mainMenu.admin"),
      icon: <AiOutlinePoundCircle color={colorPrimary} />,
      children: buildSystemAdminSectionChildren(deps),
    },
    !isProd &&
      hasRole(["Internal"]) && {
        key: "demo-hub",
        label: t("common:components.mainMenu.demoHub"),
        icon: <AiOutlineExperiment color={colorPrimary} />,
        children: [
          {
            key: "configuration",
            label: (
              <Link
                to={`${pathsSchema.demoHub.index}/${pathsSchema.demoHub.settings}`}
              >
                {t("common:components.mainMenu.configuration")}
              </Link>
            ),
          },
          {
            key: "dashboard",
            label: (
              <a
                href={`${pathsSchema.demoHub.index}/${pathsSchema.demoHub.customerDashboard}`}
              >
                {t("common:components.mainMenu.customerDashboard")}
              </a>
            ),
          },
          {
            key: "storefront",
            label: (
              <a href={pathsSchema.demoHub.storefront}>
                {t("common:components.mainMenu.storefront")}
              </a>
            ),
          },
          {
            key: "ledgerSimulation",
            label: (
              <a href={pathsSchema.demoHub.ledgerEngine}>
                {t("common:components.mainMenu.ledgerSimulation")}
              </a>
            ),
          },
          {
            key: "badgerBank",
            label: (
              <a href={pathsSchema.demoHub.badger}>
                {t("common:components.mainMenu.badgerBank")}
              </a>
            ),
          },
        ],
      },
    !isProd &&
      hasRole(["Internal"]) && {
        key: "reference-data",
        label: t("common:components.mainMenu.referenceData"),
        icon: <AiOutlinePoundCircle color={colorPrimary} />,
        children: [
          {
            key: "currencies",
            label: (
              <Link
                to={pathsSchema.commonServices.referenceData.currencies.index}
              >
                {t("common:labels.currencies")}
              </Link>
            ),
          },
        ],
      },
    !isProd &&
      hasRole(["Internal", "Internal Read Only"]) && {
        key: "treasury",
        label: t("common:components.mainMenu.treasury"),
        icon: <AiOutlineDollar color={colorPrimary} />,
        children: [
          {
            key: "offset-accounts",
            label: (
              <Link to={pathsSchema.treasury.offsetAccounts.index}>
                {t("common:components.mainMenu.offsetAccounts")}
              </Link>
            ),
          },
        ],
      },
  ]);
};
